import * as React from "react";
import {
  CellInfo,
  TableCellRenderer
} from "react-table";
import styled, { css } from "styled-components";
import _ from "lodash";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { CustomTableColumn } from "src/library/components/Tables/Table";
import TableLink from "src/library/components/Tables/TableLink";
import { Text } from "src/library/components/Text";
import { ImageWrapper } from "src/core/shared/components/S3";
import * as globalTableColumns from "src/core/shared/components/TableGlobalColumns";
import {
  appliedDiscountTypePrice,
  formatPrice
} from "src/core/prices/utils/price-helpers";
import BrandedImage from "src/core/shared/components/BrandedImage";
import { IRole } from "src/core/shared/api/roles.api";
import { IProductMaybeWithSubstitution } from "src/core/products/utils";
import LeadTimeGroupFilter from "src/core/products/components/LeadTimeGroupFilter";
import { IDrop } from "src/core/drops/api/drops.api";
import { InstalledModuleType } from "src/utils/constants";
import { IProduct } from "src/core/products/api/products.api";
import { IOrderVariant } from "src/core/orders/api/orders.api";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import ProductStatus from "src/core/products/components/ProductStatus";
import OrderVariantLabel from "src/core/orders/components/orderVariantLabel";

export interface IProductTable {
  GridViewComponent?: (product: IProductMaybeWithSubstitution) => React.ReactNode;
  ActionButton?: TableCellRenderer;
  currentRole: IRole | null;
  pricesVisible: boolean;
  terminology: ITerminology;
  omitColumns?: ProductsTableColumnAcccessors[];
  drop?: IDrop | null;
  installedModules?: InstalledModuleType[];
}

export enum ProductsTableColumnAcccessors {
  Image = "image",
  Description = "description",
  CreatedAt = "createdAt",
  LeadTimeGroup = "leadTimeGroup",
  Prices = "prices"
}
// TODO Select all to be done with the bulk actions task
export const productTableColumns = (params: IProductTable): CustomTableColumn[] => {
  const ProductColumnsAdmin: CustomTableColumn[] = [
    globalTableColumns.id(),
    {
      Header: "Image",
      accessor: "image",
      width: 80,
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        return (
          <ImageWrapper className={cellProps.value ? "image-wrapper hasImage" : "placeholder image-wrapper"}>
            <BrandedImage
              s3Key={cellProps.value}
              alt={cellProps.original.description}
              level="public"
            />
          </ImageWrapper>
        );
      }
    },
    globalTableColumns.standardText({
      Header: "Name",
      accessor: "description",
      width: 250,
      Cell: (cellProps: CellInfo) => {
        if (cellProps.value) {
          return (
            <TableLink to={`/products/${cellProps.original.id}`}>
              {cellProps.value}
            </TableLink>
          );
        }

        return null;
      }
    }),
    globalTableColumns.date({
      header: "Created",
      accessor: "createdAt",
      show: false,
      format: "Do MMM YYYY"
    }),
    globalTableColumns.standardText({
      Header: params.terminology?.partNumber.singular.text || "Part number",
      accessor: "partNumber",
      width: 120
    }),
    globalTableColumns.productStatus(),
    globalTableColumns.standardText({
      Header: "Lead time group",
      accessor: "leadTimeGroupId",
      minLength: 1,
      filterable: false,
      sortable: false,
      width: 150,
      Cell: (cellProps: CellInfo) => {
        if (typeof cellProps.value === "object") {
          return (
            <Text>
              {cellProps.value?.customerDescription || cellProps.value?.name}
            </Text>
          );
        } else if (cellProps.value === null) {
          return (
            <Text>
              Made To Order
            </Text>
          );
        }

        return (
          <Text>
            {cellProps.original?.leadTimeGroup?.name || ""}
          </Text>
        );
      },
      Filter: LeadTimeGroupFilter
    }),
    globalTableColumns.actions({ cell: params.ActionButton }),
    {
      Header: () => <></>,
      accessor: "grid",
      resizable: false,
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        if (params.GridViewComponent)
          return params.GridViewComponent(cellProps.original);

        return;
      }
    }
  ];

  const ProductColumnsCustomer: CustomTableColumn[] = [
    {
      accessor: "image",
      width: 80,
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        return (
          <ImageWrapper className={cellProps.value ? "image-wrapper split-left hasImage" : "placeholder image-wrapper split-left"}>
            <BrandedImage
              s3Key={cellProps.value}
              alt={cellProps.original.description}
              level="public"
            />
          </ImageWrapper>
        );
      }
    },
    globalTableColumns.standardText({
      Header: "Name",
      accessor: "description",
      width: 250,
      Cell: (cellProps: CellInfo) => {
        if (cellProps.value) {
          return (
            <TableLink to={`/products/${cellProps.original.id}`}>
              {cellProps.value}
            </TableLink>
          );
        }

        return null;
      }
    }),
    globalTableColumns.actions({ cell: params.ActionButton }),
    {
      Header: () => <></>,
      accessor: "grid",
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        if (params.GridViewComponent)
          return params.GridViewComponent(cellProps.original);

        return;
      }
    }
  ];

  if (params.pricesVisible) {
    ProductColumnsCustomer.splice(2, 0, {
      Header: "Price",
      accessor: "prices",
      minWidth: 100,
      width: 100,
      Cell: (cellProps: CellInfo) => {
        const originalPrice = cellProps.value[ 0 ].price;

        if (cellProps.value && cellProps.value[ 0 ]) {
          // if discount type exists, apply before formatting otherwise use the original price
          const priceToFormat = appliedDiscountTypePrice(originalPrice, params.drop?.discount);
          const price = formatPrice(priceToFormat, 2, params.drop?.priceCode?.currency);

          return (
            <PriceCell>
              {`£${price}`}

              {" "}

              <sub>
                Each
              </sub>
            </PriceCell>
          );
        }

        return null;
      }
    });

    ProductColumnsAdmin.splice(2, 0, {
      Header: "Price",
      accessor: "prices",
      filterable: false,
      sortable: false,
      width: 80,
      Cell: (cellProps: CellInfo) => {
        const originalPrice = cellProps.value[ 0 ].price;

        if (cellProps.value && cellProps.value[ 0 ]) {
          // if discount type exists, apply before formatting otherwise use the original price
          const priceToFormat = appliedDiscountTypePrice(originalPrice, params.drop?.discount);

          return formatPrice(priceToFormat, 2, params.drop?.priceCode?.currency);
        }

        return null;
      }
    });
  }

  if (params.currentRole) {
    if (params.currentRole.id === 3) {
      if (params.omitColumns) {
        return _.filter(ProductColumnsCustomer, column => !params?.omitColumns?.includes(column.accessor as ProductsTableColumnAcccessors));
      }

      return ProductColumnsCustomer;
    } else {
      if (params.omitColumns) {
        return _.filter(ProductColumnsAdmin, column => !params?.omitColumns?.includes(column.accessor as ProductsTableColumnAcccessors));
      }
    }
  }

  return ProductColumnsAdmin;
};

const PriceCell = styled.p`
  ${({ theme }) => css`
    color: ${theme.colors.primaryDark};
    font-weight: ${theme.fonts.weights.bold};
    font-size: ${theme.fonts.sizes.h2};
  `}
`;

export interface IProductTableColumns{
  GridViewComponent?: (product: IProductMaybeWithSubstitution) => React.ReactNode;
  currentRole: IRole | null;
  pricesVisible: boolean;
  terminology: ITerminology;
  omitColumns?: ProductsTableColumnAcccessors[];
  drop?: IDrop | null;
  installedModules?: InstalledModuleType[];
  selectedColumns?: ProductAvailableTableColumn[];
  //the order variants available to use
  possibleOrderVariants?: IOrderVariant[]
  actionButton: (info: CellContext<IProduct, unknown>) => React.ReactNode;
}

export type ProductAvailableTableColumn = keyof AvailableOrderColumns;

// used to determine what columns can be rendered
interface AvailableOrderColumns {
  id: ColumnDef<IProduct, unknown>;
  image: ColumnDef<IProduct, unknown>;
  name: ColumnDef<IProduct, unknown>;
  createdAt: ColumnDef<IProduct, unknown>;
  updatedAt: ColumnDef<IProduct, unknown>;
  partNumber: ColumnDef<IProduct, unknown>;
  status: ColumnDef<IProduct, unknown>;
  price: ColumnDef<IProduct, unknown>;
  leadTimeGroup: ColumnDef<IProduct, unknown>;
  orderVariant: ColumnDef<IProduct, unknown>;
  actions: ColumnDef<IProduct, unknown>;
}

export const getProductTableColumns = (params: IProductTableColumns): Array<ColumnDef<IProduct, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IProduct>();
  const columnHelper = createColumnHelper<IProduct>();
  let columns: Array<ColumnDef<IProduct, unknown>>;

  const availableColumns: AvailableOrderColumns = {
    id: customColumnHelper.numberColumn(columnHelper, "id", {
      header: "Id",
      size: 70,
      minSize: 70,
      enableColumnFilter: true
    }, { disableNumberFormatting: true }),
    image: customColumnHelper.standardTextColumn(columnHelper, "image", {
      header: "Image",
      enableColumnFilter: false,
      enableSorting: false,
      cell: info => {
        return (
          <BrandedImage
            s3Key={info.row.original.image}
            alt={info.row.original.description}
            level="public"
          />
        );
      }
    }),
    name: customColumnHelper.standardTextColumn(columnHelper, "description", { header: "Name" }),
    partNumber: customColumnHelper.standardTextColumn(columnHelper, "partNumber", { header: "Part Number" }),
    status: customColumnHelper.standardTextColumn(columnHelper, "currentStatus.status", {
      header: "Status",
      cell: info => (
        <StatusWrapper>
          <ProductStatus status={info.row.original.currentStatus?.status} />
        </StatusWrapper>
      ),
      enableColumnFilter: false,
      enableSorting: false
    }),
    price: customColumnHelper.standardTextColumn(columnHelper, "prices", {
      header: "price",
      size: 80
    }),
    leadTimeGroup: customColumnHelper.standardTextColumn(columnHelper, "leadTimeGroupId", {
      header: "Lead time group",
      cell: info => {
        if (typeof info.row.original.leadTimeGroup === "object") {
          return (
            <Text>
              {info.row.original.leadTimeGroup?.customerDescription || info.row.original.leadTimeGroup?.name}
            </Text>
          );
        } else if (info.row.original.leadTimeGroup === null) {
          return (
            <Text>
              Made To Order
            </Text>
          );
        }

        return (
          <Text>
            {info.row.original.leadTimeGroup || ""}
          </Text>
        );
      },
      enableColumnFilter: false,
      enableSorting: false
    }),
    orderVariant: customColumnHelper.standardTextColumn(columnHelper, "orderVariantId", {
      header: params.terminology.orderVariant?.singular.text ?? "Order Variant",
      cell: info => {
        return (info.row.original.orderVariant && <OrderVariantLabel orderVariant={info.row.original.orderVariant} />);
      },
      meta: {
        filter: {
          filterType: "select",
          options: params.possibleOrderVariants?.map(ov => {
            return {
              text: ov.name,
              value: ov.id.toString()
            };
          })
        }
      }
    }),
    createdAt: customColumnHelper.dateColumn(columnHelper, "createdAt", { header: "Created" }),
    updatedAt: customColumnHelper.dateColumn(columnHelper, "updatedAt", { header: "Updated" }),

    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})
  };

  // only return selected columns if requested
  if (params.selectedColumns) {
    return params.selectedColumns.map(requestedColumn => availableColumns[ requestedColumn ]);
  } else {
  // otherwise return all by default
    columns = Object.values(availableColumns);
  }

  if (!params.possibleOrderVariants || params.possibleOrderVariants.length < 2) {
    const variantColumnI = columns.findIndex(col => col.id === "orderVariantId");

    if (variantColumnI > -1) {
      columns.splice(variantColumnI, 1);
    }
  }

  if (!params.drop) {
    const priceColumnI = columns.findIndex(col => col.id === "prices");

    if (priceColumnI > -1) {
      columns.splice(priceColumnI, 1);
    }
  }

  return columns;
};

const StatusWrapper = styled.div`
${({ theme }) => css`
  font-size: ${theme.fonts.sizes.h4}
`}`;
